import React, { useState } from 'react';
import { connect } from 'react-redux';

import amplitude from 'amplitude-js';
import { toggleSpinner, updateMessages } from 'apps/auth/actions/appAction';
import { updateCardsList } from 'apps/auth/actions/cardsAction';
import { toggleChat } from 'apps/auth/actions/chatAction';
import { addExtras, setExtra, updateExtraByName } from 'apps/auth/actions/extraAction';
import { setNewForm } from 'apps/auth/actions/formAction';
import { updatePageMessages } from 'apps/auth/actions/messageAction';
import { Button } from 'apps/auth/components/common/Button/Button';
import { InfoPage } from 'apps/auth/components/common/Layout/InfoPage/InfoPage';
import PreCheckPopup from 'apps/auth/components/common/send/popups/Precheck/PreCheckPopup';
import OccupationScreen from 'apps/auth/components/profile/aml/OccupationScreen';
import ProfileAmlLevelDocs from 'apps/auth/components/profile/aml/ProfileAmlLevelDocs';
import { ProfileAmlLevelInfo } from 'apps/auth/components/profile/aml/ProfileAmlLevelInfo';
import ProfileAmlSsnSelect from 'apps/auth/components/profile/aml/ProfileAmlSsnSelect';
import OowModal from 'apps/auth/components/send/Ach/OowModal';
import OnboardingQuestionnaireModal from 'apps/auth/components/send/modals/OnboardingQuestionnaireModal';
import ModalComponent from 'apps/auth/components/utils/ModalComponent';
import ModalWithTimer from 'apps/auth/components/utils/ModalWithTimer';
import { INCREASE_LIMITS_CONTINUE_CLICKED } from 'apps/auth/constants/amplitude/AmplitudeEvents';
import { GT_LEVEL } from 'apps/auth/constants/amplitude/AmplitudeParamName';
import { SEND_FORM_NAME } from 'apps/auth/constants/formName';
import { usePageMessages } from 'apps/auth/hooks/selectors';
import {
    ERROR,
    FAILED,
    INFO,
    OCCUPATION,
    OOW,
    PLAID,
    PROOF_ID,
    requestOow,
    requestPlaid,
    requestSsnOrItin,
    SSN_CONFIRM,
    SUCCESS,
} from 'apps/auth/services/achService';

import MainPlaidModal from './plaid/MainPlaidModal';

export const MainACHModal = (props) => {
    const { occupationSubmitSuccess } = usePageMessages();

    const initialState = props?.extra?.plaidAccountAction ? PLAID : INFO;
    const [screen, setScreen] = useState(initialState);
    const [errorText, setErrorText] = useState('');
    const expectedGTLevel = props.extra.profile?.amlLimit?.currentLimit + 1;

    const onClickContinue = () => {
        expectedGTLevel ? amplitude.getInstance().logEvent(INCREASE_LIMITS_CONTINUE_CLICKED, { [GT_LEVEL]: expectedGTLevel }) : null;
        if (props.extra.canShowAmlLevelUpgrade) {
            setScreen(SSN_CONFIRM);
        } else if (props.extra.needSecretQuestionsCheck && props.extra.secretQuestionsFlow) {
            if (props.extra.secretQuestionsFlow?.errorMessage) {
                setErrorText(props.extra.secretQuestionsFlow.errorMessage);
                setScreen(ERROR);
            } else if (props.extra.secretQuestionsFlow?.questionBeans?.length > 0) {
                setScreen(OOW);
            }
        } else {
            setScreen(PROOF_ID);
        }
    };
    const onClickNotNow = () => {
        props.modalRef?.current?.close();
    };

    const onClickSuccess = () => {
        if (props?.extra?.plaidAccountAction && !props?.extra?.checksSuccess) {
            setScreen(PLAID);
        } else {
            if (props?.extra?.precheck) {
                const openPrecheckPopup = () => {
                    props.popupRef?.current?.open(<PreCheckPopup popupRef={props.popupRef} formName={SEND_FORM_NAME} />);
                };
                if (props.extra?.useQuestionnaireScreen && props.extra?.questionnaire?.fields?.length > 0) {
                    props.modalRef?.current?.open(<OnboardingQuestionnaireModal modalRef={props.modalRef} onSubmitSuccess={openPrecheckPopup} />);
                    return;
                }
                props.modalRef?.current?.close();
                openPrecheckPopup();
            } else {
                props.modalRef?.current?.close();
            }
        }
    };

    const onOccupationSubmitHandler = (_fields, _data) => {
        props.toggleModalWithTimer(true, occupationSubmitSuccess, () => {
            setScreen(INFO);
        });
    };

    const onClickContinueSsn = requestSsnOrItin(setScreen, props);
    const onClickQuestions = requestOow(setScreen, setErrorText, props);
    const onSuccessPlaid = requestPlaid(setScreen, setErrorText, props);

    const render = (currentScreen) => {
        switch (currentScreen) {
            case INFO:
                return <ProfileAmlLevelInfo insideModalComponent onClickContinue={onClickContinue} onClickNotNow={onClickNotNow} />;
            case PROOF_ID:
                return <ProfileAmlLevelDocs />;
            case OCCUPATION:
                return <OccupationScreen onSubmitHandler={onOccupationSubmitHandler} />;
            case SSN_CONFIRM:
                return <ProfileAmlSsnSelect formName={SEND_FORM_NAME} onClickContinue={onClickContinueSsn} modalRef={props.modalRef} />;
            case OOW:
                const arrayQuestions = props.extra.secretQuestionsFlow?.questionBeans;
                return (
                    <OowModal
                        maxAnswers={props.extra.secretQuestionsFlow?.maxAnswers}
                        arrayQuestions={arrayQuestions}
                        onClick={onClickNotNow}
                        skipMessage={props.pageMessages.skipQuestion}
                        onClickQuestions={onClickQuestions}
                    />
                );
            case PLAID:
                return <MainPlaidModal isShowModal={props.extra?.showAchPlaidIntro} onSuccess={onSuccessPlaid} />;
            case SUCCESS:
                return <ModalWithTimer text={props.pageMessages.successText} onClick={onClickSuccess} />;
            case FAILED:
                return (
                    <InfoPage
                        insideModalComponent={true}
                        animatedIconFail
                        headerText={props.pageMessages.errorTitle}
                        secondaryText={props.pageMessages.errorText}
                        topButtonCaption={props.pageMessages.notNow}
                        topButtonType="link"
                        topButtonLink={props.extra?.topButtonLink}
                        topButtonOnClick={onClickNotNow}
                        primaryButtonCaption={props.pageMessages.contactSupport}
                        primaryButtonOnClick={() => props.toggleChat(true)}
                    />
                );
            case ERROR:
                return (
                    <InfoPage
                        insideModalComponent={true}
                        animatedIconFail
                        mainText={errorText}
                        primaryButtonCaption={props.pageMessages.goBack}
                        primaryButtonOnClick={onClickNotNow}
                    />
                );
            default:
                return <ProfileAmlLevelInfo onClickContinue={onClickContinue} onClickNotNow={onClickNotNow} />;
        }
    };
    const isShowRightHeaderComponent = screen === SSN_CONFIRM || screen === PROOF_ID;
    return (
        <ModalComponent
            dataTestId="profile-aml-level-info-div-main"
            canCloseModal={false}
            isVerticalCentered={screen === FAILED || screen === ERROR || screen === INFO}
            rightHeaderComponent={
                isShowRightHeaderComponent && (
                    <Button type="button" size="tight" variant="tertiary" onClick={onClickNotNow} caption={props.pageMessages.notificationButton} />
                )
            }
        >
            {render(screen)}
        </ModalComponent>
    );
};
const mapStateToProps = ({ messages, form, extra }) => {
    return {
        fields: form[SEND_FORM_NAME].fields,
        pageMessages: messages.pageMessages,
        extra: extra,
    };
};
export default connect(mapStateToProps, {
    toggleSpinner,
    updateCardsList,
    updateExtraByName,
    setNewForm,
    updatePageMessages,
    toggleChat,
    setExtra,
    addExtras,
    updateMessages,
})(MainACHModal);
